import { useEffect, useCallback, useRef, useState } from 'react';
import { useMqttStore } from '@stores/mqttStore';
import * as mqttService from '@services/mqttService';

import type { RunExecutionStatus, ItemExecutionStatus } from '../types/mqtt';
import type { TestRun } from '../types';

const MAX_EVENTS = 500;

type ExecutionEvent = {
  timestamp: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  description?: string;
  stepIndex?: number;
  itemGlobalId?: string;
};

type Measurement = {
  testPoint: string;
  nominal: number | null;
  measured: number | null;
  /** Raw source text of the measured value (e.g. "0.0") — preserved for display fidelity. */
  measuredText: string | null;
  unit: string | null;
  error: number | null;
  passFail: 'PASS' | 'FAIL' | 'PENDING' | null;
  imageId: string | null;
};

type ExecutionState = {
  runStatus: RunExecutionStatus | null;
  itemStatus: ItemExecutionStatus | null;
  events: ExecutionEvent[];
  measurements: Measurement[];
  isActive: boolean;
};

const initialState: ExecutionState = {
  runStatus: null,
  itemStatus: null,
  events: [],
  measurements: [],
  isActive: false
};

/** Parse a saved TestRun's eventLog JSON into ExecutionEvent[] */
const parseSavedEvents = (eventLog: string | null): ExecutionEvent[] => {
  if (!eventLog) return [];
  try {
    const raw = JSON.parse(eventLog) as {
      timestamp: string;
      level: string;
      message: string;
      stepIndex?: number | null;
      itemGlobalId?: string | null;
      description?: string | null;
    }[];
    const levelMap: Record<string, ExecutionEvent['type']> = {
      info: 'info',
      warning: 'warning',
      error: 'error',
      success: 'success'
    };
    return raw.map(e => ({
      timestamp: e.timestamp,
      message: e.message,
      type: levelMap[e.level] ?? 'info',
      stepIndex: e.stepIndex ?? undefined,
      itemGlobalId: e.itemGlobalId ?? undefined,
      description: e.description ?? undefined
    }));
  } catch {
    return [];
  }
};

/** Parse a saved TestRun's measurementResults JSON into Measurement[] */
const parseSavedMeasurements = (
  measurementResults: string | null
): Measurement[] => {
  if (!measurementResults) return [];
  try {
    const parsed = JSON.parse(measurementResults);
    if (Array.isArray(parsed)) return parsed;
    // testPoints format from ResultMapper
    if (parsed.testPoints && Array.isArray(parsed.testPoints)) {
      return parsed.testPoints.map(
        (tp: {
          name: string;
          measuredAsFound: number | null;
          measuredAsLeft: number | null;
          measuredAsFoundText: string | null;
          measuredAsLeftText: string | null;
          unitAsFound: string | null;
          unitAsLeft: string | null;
        }) => ({
          testPoint: tp.name,
          nominal: null,
          measured: tp.measuredAsFound ?? tp.measuredAsLeft ?? null,
          measuredText: tp.measuredAsFoundText ?? tp.measuredAsLeftText ?? null,
          unit: tp.unitAsFound ?? tp.unitAsLeft ?? null,
          error: null,
          passFail: null,
          imageId: null
        })
      );
    }
    return [];
  } catch {
    return [];
  }
};

const useExecutionStatus = (runId: string | null) => {
  const isConnected = useMqttStore(state => state.isConnected);
  const stationId = useMqttStore(state => state.stationId);

  const [state, setState] = useState<ExecutionState>(initialState);
  const eventsRef = useRef<ExecutionEvent[]>([]);
  const measurementsRef = useRef<Measurement[]>([]);

  const addEvent = useCallback(
    (
      message: string,
      type: ExecutionEvent['type'] = 'info',
      extras?: Pick<ExecutionEvent, 'description' | 'stepIndex' | 'itemGlobalId'>
    ) => {
      const event: ExecutionEvent = {
        timestamp: new Date().toISOString(),
        message,
        type,
        ...extras
      };
      eventsRef.current = [
        ...eventsRef.current.slice(-(MAX_EVENTS - 1)),
        event
      ];
      setState(prev => ({ ...prev, events: eventsRef.current }));
    },
    []
  );

  const handleRunStatus = useCallback(
    (_topic: string, payload: string) => {
      if (!payload) return;
      try {
        const runStatus = JSON.parse(payload) as RunExecutionStatus;

        setState(prev => ({
          ...prev,
          runStatus,
          isActive:
            runStatus.status === 'RUNNING' || runStatus.status === 'PAUSED'
        }));

        // Only surface errors/failures — Progress tab shows normal lifecycle
        if (runStatus.status === 'FAILED') {
          addEvent(
            `Run failed: ${runStatus.errorMessage ?? 'Unknown error'}`,
            'error'
          );
        } else if (runStatus.status === 'REJECTED') {
          addEvent(
            `Run rejected: ${runStatus.errorMessage ?? 'Another run is still executing'}`,
            'error'
          );
        }
      } catch {
        addEvent('Failed to parse run status', 'error');
      }
    },
    [addEvent]
  );

  const handleItemStatus = useCallback(
    (_topic: string, payload: string) => {
      if (!payload) return;
      try {
        const itemStatus = JSON.parse(payload) as ItemExecutionStatus;
        setState(prev => ({ ...prev, itemStatus }));

        // Only surface failures — Progress tab shows normal item lifecycle
        if (itemStatus.status === 'FAILED') {
          addEvent(
            `Item ${itemStatus.itemGlobalId} failed: ${itemStatus.errorMessage ?? 'Unknown error'}`,
            'error'
          );
        }
      } catch {
        addEvent('Failed to parse item status', 'error');
      }
    },
    [addEvent]
  );

  const handleMeasurement = useCallback(
    (_topic: string, payload: string) => {
      if (!payload) return;
      try {
        const raw = JSON.parse(payload) as Measurement;
        measurementsRef.current = [...measurementsRef.current, raw];
        setState(prev => ({ ...prev, measurements: measurementsRef.current }));
      } catch {
        addEvent('Failed to parse measurement', 'error');
      }
    },
    [addEvent]
  );

  const handleStepEvent = useCallback(
    (_topic: string, payload: string) => {
      try {
        const evt = JSON.parse(payload) as {
          timestamp: string;
          level: string;
          message: string;
          stepIndex?: number | null;
          itemGlobalId?: string | null;
          description?: string | null;
        };
        const levelMap: Record<string, ExecutionEvent['type']> = {
          info: 'info',
          warning: 'warning',
          error: 'error',
          success: 'success'
        };
        addEvent(evt.message, levelMap[evt.level] ?? 'info', {
          stepIndex: evt.stepIndex ?? undefined,
          itemGlobalId: evt.itemGlobalId ?? undefined,
          description: evt.description ?? undefined
        });
      } catch {
        // ignore malformed events
      }
    },
    [addEvent]
  );

  useEffect(() => {
    if (!isConnected || !stationId || !runId) return;

    // Reset state when runId changes
    eventsRef.current = [];
    measurementsRef.current = [];
    setState(initialState);

    const statusTopic = `voltbot/${stationId}/script/run/${runId}/status`;
    const itemTopic = `voltbot/${stationId}/script/run/${runId}/item/+/status`;
    const measurementTopic = `voltbot/${stationId}/script/run/${runId}/measurement`;
    const eventTopic = `voltbot/${stationId}/script/run/${runId}/event`;

    mqttService.subscribe(statusTopic, handleRunStatus);
    mqttService.subscribe(itemTopic, handleItemStatus);
    mqttService.subscribe(measurementTopic, handleMeasurement);
    mqttService.subscribe(eventTopic, handleStepEvent);

    return () => {
      mqttService.unsubscribe(statusTopic, handleRunStatus);
      mqttService.unsubscribe(itemTopic, handleItemStatus);
      mqttService.unsubscribe(measurementTopic, handleMeasurement);
      mqttService.unsubscribe(eventTopic, handleStepEvent);
    };
  }, [
    isConnected,
    stationId,
    runId,
    handleRunStatus,
    handleItemStatus,
    handleMeasurement,
    handleStepEvent
  ]);

  const reset = useCallback(() => {
    eventsRef.current = [];
    measurementsRef.current = [];
    setState(initialState);
  }, []);

  /** Restore display state from a persisted TestRun (e.g. on page load) */
  const loadFromSaved = useCallback((run: TestRun) => {
    const events = parseSavedEvents(run.eventLog);
    const measurements = parseSavedMeasurements(run.measurementResults);
    eventsRef.current = events;
    measurementsRef.current = measurements;
    setState({
      runStatus: {
        runId: run.id,
        status: run.status as RunExecutionStatus['status'],
        currentItemIndex: 0,
        totalItems: 0,
        startedAt: run.startedAt,
        completedAt: run.completedAt,
        errorMessage: null
      },
      itemStatus: null,
      events,
      measurements,
      isActive: run.status === 'RUNNING' || run.status === 'PAUSED'
    });
  }, []);

  return { ...state, reset, loadFromSaved };
};

export default useExecutionStatus;
export type { ExecutionState, ExecutionEvent, Measurement };
